/**
 * Each screen names itself in the shell's top bar. Half these titles are data,
 * so the page has to supply them.
 *
 * A portal rather than context state: a title can contain JSX, and a context
 * write would take a fresh element as its effect dependency and loop.
 */
import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
	type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link as RouterLink } from 'react-router-dom';
import {
	Box,
	Breadcrumbs,
	Link,
	Typography,
	type SxProps,
	type Theme,
} from '@mui/material';
import { useModule } from '../features/modules/useModule';
import { crumbHref } from '../features/modules/flow';

/** One step of the trail above a page title. `to` navigates; `onClick` is for
 *  a step that is a mode of the same page (the report's print view). The last
 *  step is the page itself and is not given here — the title says it. */
export interface Crumb {
	label: string;
	to?: string;
	onClick?: () => void;
}

/* The name at the left end, the page's own actions at the right. */
type Slot = 'title' | 'acts';

const Ctx = createContext<{
	nodes: Record<Slot, HTMLElement | null>;
	attach: (slot: Slot, node: HTMLElement | null) => void;
}>({ nodes: { title: null, acts: null }, attach: () => {} });

export function PageTitleProvider({ children }: { children: ReactNode }) {
	/* State, not a ref: the slots do not exist on the first render, so pages
     inside need a second one to find them. */
	const [nodes, setNodes] = useState<Record<Slot, HTMLElement | null>>({
		title: null,
		acts: null,
	});
	const attach = useCallback((slot: Slot, node: HTMLElement | null) => {
		setNodes((current) =>
			current[slot] === node ? current : { ...current, [slot]: node },
		);
	}, []);
	const value = useMemo(() => ({ nodes, attach }), [nodes, attach]);
	return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The shell puts these where the title and the actions should appear. */
export function PageTitleSlot({
	slot,
	sx,
}: {
	slot: Slot;
	sx?: SxProps<Theme>;
}) {
	const { attach } = useContext(Ctx);
	const ref = useCallback(
		(node: HTMLElement | null) => attach(slot, node),
		[attach, slot],
	);
	return <Box sx={sx} ref={ref} />;
}

/**
 * This screen's name, rendered into the top bar. Renders nothing until the
 * shell's slot exists, so a page mounted on its own is not an error.
 */
export function PageHeader({
	kind,
	breadcrumbs,
	title,
	subtitle,
	hint,
	actions,
}: {
	/** What kind of page this is, for a title that is data. Kept out of the
	 *  <h1> so a screen reader still announces the subject. */
	kind?: ReactNode;
	/** Where this page sits: e.g. Client Documents › Eris Pharma › Sales Apr.xlsx.
	 *  Replaces "Back" links — every step is a way back. */
	breadcrumbs?: Crumb[];
	title: ReactNode;
	subtitle?: ReactNode;
	hint?: ReactNode;
	actions?: ReactNode;
}) {
	const { nodes } = useContext(Ctx);
	/* A crumb is a way back, and back is in the same module — unless the crumb
     names its module itself (a module crumb: Clients › Eris › Hire to Retire). */
	const { module } = useModule();
	return (
		<>
			{nodes.title &&
				createPortal(
					<>
						{breadcrumbs && breadcrumbs.length > 0 && (
							<Breadcrumbs
								separator="›"
								aria-label="Breadcrumb"
								sx={{
									fontSize: 'var(--fs-body)',
									lineHeight: 2,
									mb: '1px',
								}}
							>
								{breadcrumbs.map((crumb, index) =>
									crumb.to ? (
										<Link
											key={`${index}-${crumb.label}`}
											component={RouterLink}
											to={crumbHref(crumb.to, module)}
											underline="none"
											color="inherit"
											sx={{
												position: 'relative',
												display: 'inline-block',
												color: 'black',
												whiteSpace: 'nowrap',
												textDecoration: 'none',
												transition: 'color 0.2s ease',

												'&::after': {
													content: '""',
													position: 'absolute',
													left: 0,
													bottom: '-0.1px',
													width: '100%',
													height: '1px',
													backgroundColor: '#0000FF',
													transform: 'scaleX(0)',
													transformOrigin: 'left',
													transition:
														'transform 0.25s ease',
												},

												'&:hover': {
													color: '#0000FF',
												},

												'&:hover::after': {
													transform: 'scaleX(1)',
												},
											}}
										>
											{crumb.label}
										</Link>
									) : crumb.onClick ? (
										<Link
											key={`${index}-${crumb.label}`}
											component="button"
											type="button"
											underline="hover"
											color="inherit"
											onClick={crumb.onClick}
											sx={{
												fontSize: 'inherit',
												verticalAlign: 'baseline',
											}}
										>
											{crumb.label}
										</Link>
									) : (
										<Typography
											component="span"
											variant="inherit"
											key={`${index}-${crumb.label}`}
										>
											{crumb.label}
										</Typography>
									),
								)}
							</Breadcrumbs>
						)}
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 'var(--s2)',
								minWidth: 0,
							}}
						>
							{kind && (
								<Typography
									component="span"
									sx={{
										flex: '0 0 auto',
										fontSize: 'var(--fs-body)',
										fontWeight: 500,
										color: 'var(--ink-3)',
										whiteSpace: 'nowrap',
										/* A word ("File") is followed by "·"; a back button is not. */
										...(typeof kind === 'string' && {
											'&::after': {
												content: '"·"',
												ml: '6px',
											},
										}),
									}}
								>
									{kind}
								</Typography>
							)}
							<Typography
								component="h1"
								noWrap
								sx={{
									m: 0,
									fontSize: 'var(--fs-title)',
									fontWeight: 600,
									letterSpacing: 'var(--tracking-tight)',
									lineHeight: 'var(--lh-tight)',
									color: 'var(--ink)',
								}}
							>
								{title}
							</Typography>
							{hint}
						</Box>
						{subtitle && (
							<Box
								sx={{
									flex: '1 1 auto',
									minWidth: 0,
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
									fontSize: 'var(--fs-small)',
									color: 'var(--ink-3)',
									lineHeight: 'var(--lh-snug)',
									'@media (max-width: 900px)': {
										display: 'none',
									},
								}}
							>
								{subtitle}
							</Box>
						)}
					</>,
					nodes.title,
				)}
			{actions && nodes.acts && createPortal(actions, nodes.acts)}
		</>
	);
}
